import type { ShipDto } from '@/api/hooks'

/**
 * Une coque ne peut pas ÊTRE AJOUTÉE à une sortie. Deux motifs, tous deux
 * refusés côté serveur par les onze chemins de lancement :
 *   - coque hors d'état (une épave ne navigue pas) ;
 *   - branle-bas (fiche #27) : elle tient les rangs derrière les remparts.
 *
 * Ne s'applique QU'À l'ajout. Retirer reste toujours permis, sinon une coque
 * cochée que le branle-bas réquisitionne (la garnison prend les plus
 * puissantes, soit exactement celles qu'un raideur vient de choisir) devient
 * indécrochable : le compteur la compte, le serveur refuse le lancement, et
 * rien à l'écran ne permet de la lâcher.
 */
function isBlocked(ship: ShipDto | undefined): boolean {
  return ship?.seaworthy === false || ship?.mobilized === true
}

/**
 * Shared ship-selection toggle for every mission launcher's fleet picker — the
 * one place that decides whether an id enters `prev`. Rules:
 *   - already selected → remove it (deselection is always allowed);
 *   - unseaworthy hull → refuse to add (a wreck can't sail; mirrors the disabled
 *     row + the server's rejection). Extracted from 9 copy-pasted `toggleShip`
 *     bodies so the guard can't drift between launchers;
 *   - mobilized (branle-bas, fiche #27) → refuse to add: the hull is holding the
 *     line behind the ramparts, and the server rejects it too;
 *   - at the fleet cap → refuse to add and warn via `onCapExceeded` (the message
 *     differs per launcher: "Flotte au max" / "Convoi au max" / éclaireurs…);
 *   - otherwise → append (order matters: first pick is the fleet lead).
 * Pure — returns the next id list; the caller wraps it in `setState(prev => …)`.
 */
export function toggleShipSelection(
  prev: number[],
  id: number,
  dockedShips: ShipDto[],
  cap: number,
  onCapExceeded: () => void,
): number[] {
  if (prev.includes(id)) return prev.filter((x) => x !== id)
  if (isBlocked(dockedShips.find((s) => s.id === id))) return prev
  if (prev.length >= cap) {
    onCapExceeded()
    return prev
  }
  return [...prev, id]
}

/**
 * Ajout en masse (« Tout prendre »), qui passe par les MÊMES gardes que le
 * clic unitaire. Écrite ici, et pas dans les composants, précisément pour ne
 * pas ouvrir une seconde porte vers `prev` : c'est la dérive que l'extraction
 * de `toggleShipSelection` existait pour tuer.
 *
 *   - ajoute, ne remplace pas : la sélection hors filtre (celle que le bandeau
 *     d'épinglés protège) doit survivre, et le navire de tête ne doit pas changer ;
 *   - borne à `cap - prev.length`, le cap étant un total alors que la liste
 *     filtrée et le bandeau sont deux ensembles disjoints ;
 *   - saute les coques bloquées, qui restent volontairement VISIBLES dans la
 *     liste : un `slice(0, cap)` naïf sélectionnerait des épaves ;
 *   - conserve l'ordre reçu, qui est l'ordre AFFICHÉ. Conséquence assumée : le
 *     tri courant décide du navire de tête, d'où le défaut sur « arrivée ».
 *
 * @param candidateIds ids dans l'ordre où ils sont affichés
 */
export function selectManyShips(
  prev: number[],
  candidateIds: number[],
  dockedShips: ShipDto[],
  cap: number,
  onCapReached: () => void,
): number[] {
  const next = [...prev]
  let refused = false

  for (const id of candidateIds) {
    if (next.includes(id)) continue
    if (isBlocked(dockedShips.find((s) => s.id === id))) continue
    if (next.length >= cap) {
      refused = true
      break
    }
    next.push(id)
  }

  if (refused) onCapReached()
  return next
}
